"use client";

import { useTranslations } from "next-intl";
import { Paper, Stack, Text, Group, Box } from "@forestsync/ui";
import { generateLegendSteps } from "@/lib/color-ramps";

interface ColorLegendProps {
  type: "viridis" | "rdylgr";
  steps?: number;
}

/**
 * Qualitative legend for the imagery colour scale.
 *
 * Labels are relative (low/high, loss/gain) on purpose: the delivered imagery is a
 * styled render, and the analyst's value range is not known, so no Mg/ha figures
 * are shown until the raw single-band raster and its style range are delivered.
 */
export function ColorLegend({ type, steps = 10 }: ColorLegendProps) {
  const t = useTranslations("sink.imagery");
  const isBiomass = type === "viridis";

  // Generate legend steps
  const legendSteps = generateLegendSteps(type, steps);

  return (
    <Paper p="md" shadow="sm" radius="md" withBorder>
      <Stack gap="sm">
        <Text size="sm" fw={600}>
          {isBiomass ? t("biomassDensity") : t("biomassChange")}
        </Text>

        {/* Color gradient bar */}
        <Box
          style={{
            height: 20,
            borderRadius: 4,
            background: `linear-gradient(to right, ${legendSteps.map((step) => step.color).join(", ")})`,
          }}
        />

        {/* Min and max labels */}
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            {isBiomass ? t("legendLow") : t("legendLoss")}
          </Text>
          <Text size="xs" c="dimmed">
            {isBiomass ? t("legendHigh") : t("legendGain")}
          </Text>
        </Group>

        {/* Description */}
        <Text size="xs" c="dimmed">
          {isBiomass ? t("legendBiomassNote") : t("legendChangeNote")}
        </Text>
      </Stack>
    </Paper>
  );
}
